import React, { useEffect, useState } from "react";
import { Button } from "primereact/button";
import InputTextComponent from "presentation/components/Shared/InputTextComponent";
import InputDateComponent from "presentation/components/Shared/InputDateComponent";
import LinkedinCardsDescription from "./LinkedinCardsDescription";

type Props = {
  initialData?: any;
  id?: string;
  onSuccess?: () => void;
};

type LinkedinCardsForm = {
  id: string;
  title: string;
  companyName: string;
  companyLocation: string;
  jobType: string;
  listedDate: Date;
  recruiterName: string;
  recruiterProfile: string;
  cardLink: string;
  experienceLevel: string;
  functionOnJob: string;
  sectorJob: string;
  salary: string;
  description: string;
  createdAt: Date;
  modifiedAt: Date | null;
};

const LinkedinCardsForm: React.FC<Props> = ({ initialData }) => {

  const normalizeForm = (data: any = {}): LinkedinCardsForm => ({
    id: data.id ?? "",
    title: data.title ?? "",
    companyName: data.companyName ?? "",
    companyLocation: data.companyLocation ?? "",
    jobType: data.jobType ?? "",
    listedDate: data.listedDate ? new Date(data.listedDate) : new Date(),
    recruiterName: data.recruiterName ?? "",
    recruiterProfile: data.recruiterProfile ?? "",
    cardLink: data.cardLink ?? "",
    experienceLevel: data.experienceLevel ?? "",
    functionOnJob: data.functionOnJob ?? "",
    sectorJob: data.sectorJob ?? "",
    description: data.description ?? "",
    salary: data.salary ?? "",
    createdAt: data.createdAt ? new Date(data.createdAt) : new Date(),
    modifiedAt: data.modifiedAt ? new Date(data.modifiedAt) : null,
  });

  const [form, setForm] = useState<LinkedinCardsForm>(() =>
    normalizeForm(initialData)
  );

  const [showDescription, setShowDescription] = useState(false);

  useEffect(() => {
    if (initialData) {
      setForm(normalizeForm(initialData));
    }
  }, [initialData]);

  const openLink = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const isCardLinkValid =
    !!form.cardLink && form.cardLink !== "Não especificado";

  const isRecruiterProfileValid =
    !!form.recruiterProfile && form.recruiterProfile !== "Não especificado";

  const inputStyles: React.CSSProperties = {
    color: "#1e293b",
    opacity: 1,
    fontWeight: 500,
  };

  return (
    <div className="p-fluid mt-4" style={{ paddingTop: "30px" }}>

      {/* Linha 1: Título | Link da vaga */}
      <div className="grid mb-4 align-items-stretch">
        <div className="col">
          <InputTextComponent
            label="Título"
            fieldName="title"
            currentObject={form}
            getValue={form.title}
            setValue={setForm}
            isDisable
            style={inputStyles}
          />
        </div>

        <div className="col-fixed flex" style={{ width: "60px" }}>
          <Button
            type="button"
            icon="pi pi-linkedin"
            className="w-full h-full"
            tooltip="Ver vaga no LinkedIn"
            tooltipOptions={{ position: "top" }}
            disabled={!isCardLinkValid}
            onClick={() => openLink(form.cardLink)}
          />
        </div>
      </div>

      {/* Linha 2: Nome da empresa | Salário | Tipo de emprego */}
      <div className="grid mb-5">
        <div className="col-3">
          <InputTextComponent
            label="Nome da empresa"
            fieldName="companyName"
            currentObject={form}
            getValue={form.companyName}
            setValue={setForm}
            isDisable
            style={inputStyles}
          />
        </div>

        <div className="col-3">
          <InputTextComponent
            label="Salário"
            fieldName="salary"
            currentObject={form}
            getValue={form.salary}
            setValue={setForm}
            isDisable
            style={inputStyles}
          />
        </div>

        <div className="col-3">
          <InputTextComponent
            label="Tipo de emprego"
            fieldName="jobType"
            currentObject={form}
            getValue={form.jobType}
            setValue={setForm}
            isDisable
            style={inputStyles}
          />
        </div>

        <div className="col-3">
          <InputTextComponent
            label="Localização da vaga"
            fieldName="companyLocation"
            currentObject={form}
            getValue={form.companyLocation}
            setValue={setForm}
            isDisable
            style={inputStyles}
          />
        </div>
      </div>

      {/* Linha 3: Setor | Função | Nível de experiência | Data de publicação */}
      <div className="grid mb-4">
        <div className="col-3">
          <InputTextComponent
            label="Setor da vaga"
            fieldName="sectorJob"
            currentObject={form}
            getValue={form.sectorJob}
            setValue={setForm}
            isDisable
            style={inputStyles}
          />
        </div>

        <div className="col-3">
          <InputTextComponent
            label="Função na vaga"
            fieldName="functionOnJob"
            currentObject={form}
            getValue={form.functionOnJob}
            setValue={setForm}
            isDisable
            style={inputStyles}
          />
        </div>

        <div className="col-3">
          <InputTextComponent
            label="Nível de experiência"
            fieldName="experienceLevel"
            currentObject={form}
            getValue={form.experienceLevel}
            setValue={setForm}
            isDisable
            style={inputStyles}
          />
        </div>

        <div className="col-3">
          <InputDateComponent
            label="Data de publicação"
            fieldName="listedDate"
            currentObject={form}
            getValue={form.listedDate}
            setValue={setForm}
            isDisable
            style={inputStyles}
          />
        </div>
      </div>

      {/* Linha 4: Recrutador | Link do perfil do recrutador */}
      <div className="grid mb-4 align-items-stretch">
        <div className="col">
          <InputTextComponent
            label="Recrutador"
            fieldName="recruiterName"
            currentObject={form}
            getValue={form.recruiterName}
            setValue={setForm}
            isDisable
            style={inputStyles}
          />
        </div>

        <div className="col-fixed flex justify-content-center" style={{ width: "60px" }}>
          <Button
            type="button"
            icon="pi pi-linkedin"
            className="w-full h-full"
            tooltip="Ver perfil do recrutador no LinkedIn"
            tooltipOptions={{ position: "top" }}
            disabled={!isRecruiterProfileValid}
            onClick={() => openLink(form.recruiterProfile)}
          />
        </div>
      </div>

      {/* Linha 5: Descrição da vaga */}
      <div className="grid mb-4">
        <div className="col-12">
          <Button
            type="button"
            label="Ver descrição completa"
            icon="pi pi-align-left"
            className="p-button-outlined w-auto"
            onClick={() => setShowDescription(true)}
          />
        </div>
      </div>

      <LinkedinCardsDescription
        visible={showDescription}
        onHide={() => setShowDescription(false)}
        description={form.description}
        title={form.title}
      />
    </div>
  );
};

export default LinkedinCardsForm;